import React from 'react';
import { Player, ActiveEffect } from '../../types';
import { CharacterCardHeader } from './CharacterCardHeader';
import { StatsEditor, StatsUpdate } from './StatsEditor';
import { ActiveEffectsPanel } from './ActiveEffectsPanel';
import { KeywordsPanel } from './KeywordsPanel';
import { ImmunitiesPanel } from './ImmunitiesPanel';
import { AbilityCard } from './AbilityCard';

export interface CharacterCardContainerProps {
  /** The player whose character to display */
  player: Player;
  /** Whether this is the current user's character (enables editing) */
  isOwnCharacter: boolean;
  /** Whether this player has the current turn */
  isCurrentTurn: boolean;
  /** Callback when stats are updated */
  onStatsUpdate?: (updates: StatsUpdate) => Promise<void>;
  /** Callback when an effect is added */
  onEffectAdd?: (effect: ActiveEffect) => void;
  /** Callback when an effect is removed */
  onEffectRemove?: (index: number) => void;
  /** Callback when an effect's duration is updated */
  onEffectDurationUpdate?: (index: number, duration: number) => void;
  /** Callback when an ability is used */
  onAbilityUse?: (abilityId: string) => void;
  /** Optional className for additional styling */
  className?: string;
}

/**
 * CharacterCard Container Component
 * 
 * Composes all character card sub-components with styling matching the codex detail page:
 * - CharacterCardHeader (name, type, phases)
 * - StatsEditor (health, will, attack, defense, speed)
 * - ActiveEffectsPanel (status effects)
 * - KeywordsPanel (character keywords)
 * - ImmunitiesPanel (character immunities)
 * - AbilityCard list (character abilities with sub-abilities)
 * 
 * Requirements: 3.1, 3.3, 3.4
 */
export const CharacterCardContainer: React.FC<CharacterCardContainerProps> = ({
  player,
  isOwnCharacter,
  isCurrentTurn,
  onStatsUpdate,
  onEffectAdd,
  onEffectRemove,
  onEffectDurationUpdate,
  onAbilityUse,
  className = '',
}) => {
  const { character, stats } = player;

  // Handle case where no character is selected
  if (!character) {
    return (
      <div 
        className={`character-card-container bg-gray-800/30 rounded-2xl p-8 border border-gray-700/50 backdrop-blur-sm ${className}`}
        data-testid="character-card-no-character"
      >
        <div className="text-center py-12">
          <div className="text-5xl mb-4 opacity-50">👤</div>
          <h3 className="text-xl font-semibold text-gray-400">No Character Selected</h3>
          <p className="text-sm text-gray-500 mt-3">
            {player.username} has not selected a character yet.
          </p>
        </div>
      </div>
    );
  }

  // Handle stats update
  const handleStatsUpdate = async (updates: StatsUpdate) => {
    if (onStatsUpdate) {
      await onStatsUpdate(updates);
    }
  };

  // Handle ability use
  const handleAbilityUse = (abilityId: string) => {
    if (onAbilityUse) {
      onAbilityUse(abilityId);
    }
  };

  return (
    <div 
      className={`character-card-container bg-gray-800/30 rounded-2xl border border-gray-700/50 overflow-hidden backdrop-blur-sm shadow-xl ${className}`}
      data-testid="character-card"
      data-player-id={player.userId}
      data-character-id={character.characterId}
    >
      {/* Header Section - styled like codex detail header */}
      <div className="character-card-header-section p-6 pb-5 border-b-2 border-gray-700/50 bg-gradient-to-b from-gray-800/50 to-transparent">
        <CharacterCardHeader
          character={character}
          isCurrentTurn={isCurrentTurn}
          phasesRemaining={stats.phasesRemaining}
        />
      </div>

      {/* Scrollable Content Area */}
      <div className="character-card-content p-6 space-y-6 max-h-[calc(100vh-300px)] overflow-y-auto">
        {/* Stats Editor - styled section */}
        <div className="detail-section">
          <StatsEditor
            stats={stats}
            baseStats={character.baseStats}
            isEditable={isOwnCharacter}
            onSave={handleStatsUpdate}
          />
        </div>

        {/* Character Description */}
        {character.description && (
          <div className="detail-section">
            <p className="character-description">{character.description}</p>
          </div>
        )}

        {/* Active Effects - styled section */}
        <div className="detail-section">
          <ActiveEffectsPanel
            effects={stats.activeEffects}
            isEditable={isOwnCharacter}
            onAddEffect={onEffectAdd}
            onRemoveEffect={onEffectRemove}
            onUpdateDuration={onEffectDurationUpdate}
          />
        </div>

        {/* Keywords - styled section */}
        {character.keywords && character.keywords.length > 0 && (
          <div className="detail-section">
            <h3 className="section-title text-base font-semibold text-white mb-3 pb-2 border-b border-gray-700/50">
              Keywords & Traits
            </h3>
            <KeywordsPanel keywords={character.keywords} />
          </div>
        )}

        {/* Immunities - styled section */}
        {character.immunities && character.immunities.length > 0 && (
          <div className="detail-section">
            <h3 className="section-title text-base font-semibold text-white mb-3 pb-2 border-b border-gray-700/50">
              Immunities
            </h3>
            <ImmunitiesPanel immunities={character.immunities} />
          </div>
        )}

        {/* Abilities Section - styled like codex abilities */}
        <div className="detail-section abilities-section" data-testid="abilities-section">
          <h3 className="section-title text-base font-semibold text-white mb-4 pb-2 border-b border-gray-700/50">
            Abilities ({character.abilities.length})
          </h3>
          <div className="space-y-4" data-testid="abilities-list">
            {character.abilities.map((ability) => (
              <AbilityCard
                key={ability.abilityId}
                ability={ability}
                remainingUses={stats.abilityUsages?.[ability.abilityId]}
                isEditable={isOwnCharacter}
                onUseAbility={() => handleAbilityUse(ability.abilityId)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Also export as CharacterCard for convenience
export { CharacterCardContainer as CharacterCard };
